import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomDataGrid from '../../../../shared/components/CustomDataGrid';
import { handleError } from '../../../../shared/functions/handleError';
import { useEmployeeExpensesColumns } from '../../../../shared/hooks/useEmployeeExpensesColumns';
import { usePagination } from '../../../../shared/hooks/usePagination';
import { ITeamContributions } from '../../../../shared/interfaces/team-contributions/ITeamContributions';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { AdminEmployeeService } from '../../employees/service';

interface IProps {
  isUpdate?: boolean;
  control: Control<ITeamContributions>;
  watch: UseFormWatch<ITeamContributions>;
  setValue?: UseFormSetValue<ITeamContributions>;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const TeamContributionsForm = ({
  isUpdate,
  control,
  watch,
  setValue,
}: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination } = usePagination({
    perPage: 0,
  });
  const [users, setUsers] = useState<IUser[] | undefined>(undefined);

  const columns = useEmployeeExpensesColumns(setValue);

  useEffect(() => {
    AdminEmployeeService.getAll(pagination)
      .then((response) => {
        setUsers(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination]);
  return (
    <Card>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message}
                value={value ?? ''}
                onChange={onChange}
                label={t('general.name')}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={'price'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message}
                value={value ?? ''}
                type={'number'}
                onChange={onChange}
                label={t('expenses.price')}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={'userIds'}
            control={control}
            render={({
              field: { onChange, value = [] },
              fieldState: { error },
            }) => {
              const allUserIds = users?.map((user) => user.id);
              const isAllSelected =
                value?.length === users?.length && users?.length > 0;
              return (
                <FormControl fullWidth>
                  <InputLabel id="user-label">{t('general.users')}</InputLabel>
                  <Select
                    label={t('general.users')}
                    labelId="user-label"
                    fullWidth
                    id="user-select"
                    multiple
                    value={value}
                    input={<OutlinedInput label={t('general.users')} />}
                    renderValue={() =>
                      users
                        ?.filter((user) => value?.includes(user.id))
                        ?.map((user) => `${user.firstName} ${user.lastName}`)
                        ?.join(', ')
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                          width: 250,
                        },
                      },
                    }}
                    onChange={(event) => {
                      const selectedValues = event.target.value;

                      if ((selectedValues as string)?.includes('all')) {
                        onChange(isAllSelected ? [] : allUserIds);
                      } else {
                        onChange(selectedValues);
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <Checkbox checked={isAllSelected} />
                      <ListItemText primary={t('general.selectAll')} />
                    </MenuItem>
                    {/* User Options */}
                    {users?.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Checkbox checked={value?.includes(user.id)} />
                        <Avatar
                          alt={`${user.firstName} logo`}
                          sx={{ margin: '0 6px', width: 28, height: 28 }}
                          src={user?.profilePhoto?.thumbnail}
                        />
                        {user.firstName} {user.lastName}
                      </MenuItem>
                    ))}
                  </Select>

                  {!!error?.message?.length && (
                    <FormHelperText>{error?.message}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
        </Grid>
        {isUpdate && (
          <Grid size={12}>
            <CustomDataGrid
              rows={watch('users') ?? []}
              columns={columns}
              loading={users === undefined}
              hideFooterPagination
            />
          </Grid>
        )}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={'contained'}
          color={'secondary'}
          sx={{ marginTop: '20px' }}
          type={'button'}
          onClick={() => navigate(-1)}
        >
          {t('button.cancel')}
        </Button>
        <Button
          variant={'contained'}
          sx={{ margin: '20px 0 0 10px' }}
          type={'submit'}
        >
          {t(isUpdate ? 'button.update' : 'button.create')}
        </Button>
      </Box>
    </Card>
  );
};

export default TeamContributionsForm;
